import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowRight, Play } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { videoSource, youTubeThumb } from "@/lib/youtube";
import { getJSON, resolveMedia } from "@/lib/api";

const PeekThumb = ({ item }) => {
  const src = videoSource(item.video_url);
  const poster = resolveMedia(item.poster);
  const ytThumb = src.kind === "youtube" ? youTubeThumb(item.video_url) : null;
  const thumb = poster || ytThumb;
  return (
    <div className="relative aspect-video overflow-hidden rounded-xl bg-black/40">
      {src.kind === "mp4" && !poster ? (
        <video
          className="absolute inset-0 h-full w-full object-cover"
          src={resolveMedia(src.src)}
          muted
          loop
          autoPlay
          playsInline
          preload="metadata"
          aria-hidden="true"
        />
      ) : thumb ? (
        <img src={thumb} alt={item.title} className="absolute inset-0 h-full w-full object-cover" />
      ) : (
        <div className="absolute inset-0 bg-white/5" />
      )}
      <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
    </div>
  );
};

/*
  Feature band on /growth that drives people to the Video Gallery (/gallery).
  Shows a live thumbnail peek of the newest reel items.
*/
export const ReelBand = () => {
  const { data: items = [] } = useQuery({
    queryKey: ["gallery"],
    queryFn: () => getJSON("/gallery"),
  });
  const peek = items.slice(0, 4);

  return (
    <section className="bg-[var(--charcoal)] py-24 text-white md:py-32" data-testid="growth-reel-band">
      <div className="mx-auto grid max-w-7xl items-center gap-14 px-6 md:px-12 lg:grid-cols-2 lg:gap-20">
        <div>
          <Reveal>
            <span className="text-xs font-semibold uppercase tracking-[0.18em] text-[var(--mint)]">
              The Reel
            </span>
          </Reveal>
          <Reveal delay={80}>
            <h2 className="mt-6 t-section text-white">See the work, not the pitch.</h2>
          </Reveal>
          <Reveal delay={140}>
            <p className="mt-5 max-w-lg text-base leading-relaxed text-white/70 sm:text-lg">
              A gallery of what we actually produce — ad creative, motion, and AI
              avatar video. Watch the reel.
            </p>
          </Reveal>
          <Reveal delay={200}>
            <Link
              to="/gallery"
              className="mt-9 inline-flex items-center gap-2 rounded-full bg-[var(--mint)] px-7 py-3.5 text-sm font-semibold text-[var(--charcoal)] transition-transform duration-300 hover:-translate-y-0.5"
              data-testid="growth-reel-cta"
            >
              Watch the reel <ArrowRight size={16} />
            </Link>
          </Reveal>
        </div>

        {peek.length > 0 && (
          <Reveal delay={160}>
            <Link to="/gallery" className="group relative block" aria-label="Watch the reel">
              <div className="grid grid-cols-2 gap-4" data-testid="growth-reel-peek">
                {peek.map((item) => (
                  <PeekThumb key={item.id} item={item} />
                ))}
              </div>
              <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
                <span className="flex h-16 w-16 items-center justify-center rounded-full bg-[var(--mint)] text-[var(--charcoal)] shadow-lg transition-transform duration-300 group-hover:scale-105">
                  <Play size={24} fill="currentColor" />
                </span>
              </div>
            </Link>
          </Reveal>
        )}
      </div>
    </section>
  );
};
